<template>
  <aside :class="['vtm-nav', { 'vtm-nav--collapsed': collapse }]">
    <!-- 品牌 → 点击回到首页仪表盘 -->
    <div class="vtm-nav__brand" @click="goHome" role="button" tabindex="0" @keyup.enter="goHome">
      <!-- 白色标志直接置于深色导航栏（亮/暗主题下侧栏均为深色，无需白色底板） -->
      <!-- 收起时换成方形标记，横版锁形图在 64px 宽度下会被压得无法辨认 -->
      <img
        :src="collapse ? markLogo : fullLogo"
        alt="VTM Vending"
        class="vtm-nav__logo"
      />
    </div>

    <!-- 菜单快速查找 -->
    <div v-if="!collapse" class="vtm-nav__search">
      <i class="el-icon-search"></i>
      <input
        v-model.trim="menuQuery"
        type="text"
        :placeholder="$t('aside.searchMenu')"
        :aria-label="$t('aside.searchMenu')"
      />
      <button v-if="menuQuery" type="button" class="vtm-nav__search-clear" aria-label="clear" @click="menuQuery = ''">
        <i class="el-icon-close"></i>
      </button>
    </div>

    <vue-scroll>
      <nav class="vtm-nav__items" aria-label="Main navigation">
        <!-- 置顶必备项：首页 / 我的设备 / 订单 -->
        <router-link
          v-for="item in zones.pinned"
          :key="item.id"
          :to="item.link"
          :class="['vtm-nav__item', 'vtm-nav__item--pinned', { 'is-active': isActive(item.link) }]"
          :title="collapse ? item.name : ''"
        >
          <i :class="['vtm-nav__icon', item.icon || 'el-icon-document']"></i>
          <span v-if="!collapse" class="vtm-nav__label">{{ item.name }}</span>
          <span v-if="item.badge && !collapse" class="vtm-nav__badge">{{ item.badge }}</span>
        </router-link>

        <!-- 运营者工具分组 -->
        <template v-for="group in zones.operator">
          <!-- 单链接分组不显示组标题（点击即直达） -->
          <div v-if="group.label && group.items.length > 1 && !collapse" :key="'g' + group.id" class="vtm-nav__group">{{ group.label }}</div>
          <div v-else-if="group.label && group.items.length > 1 && collapse" :key="'gd' + group.id" class="vtm-nav__divider"></div>
          <router-link
            v-for="leaf in group.items"
            :key="leaf.id"
            :to="leaf.link"
            :class="['vtm-nav__item', { 'is-active': isActive(leaf.link) }]"
            :title="collapse ? leaf.name : ''"
          >
            <i :class="['vtm-nav__icon', leaf.icon || 'el-icon-document']"></i>
            <span v-if="!collapse" class="vtm-nav__label">{{ leaf.name }}</span>
          </router-link>
        </template>

        <!-- 管理员专属区（可折叠） -->
        <template v-if="zones.admin.length">
          <!-- 展开态：带折叠头 -->
          <template v-if="!collapse">
            <button type="button" class="vtm-nav__admintoggle" :aria-expanded="String(adminOpen)" @click="toggleAdmin">
              <i class="vtm-nav__icon el-icon-s-tools"></i>
              <span class="vtm-nav__label">{{ $t("nav.administration") }}</span>
              <i :class="['vtm-nav__chev', adminOpen ? 'el-icon-arrow-up' : 'el-icon-arrow-down']"></i>
            </button>
            <!-- 管理区链接已不多：不再显示分组小标题，平铺展示 -->
            <div v-show="adminOpen" class="vtm-nav__adminbody">
              <template v-for="group in zones.admin">
                <router-link
                  v-for="leaf in group.items"
                  :key="leaf.id"
                  :to="leaf.link"
                  :class="['vtm-nav__item', 'vtm-nav__item--nested', { 'is-active': isActive(leaf.link) }]"
                >
                  <i :class="['vtm-nav__icon', leaf.icon || 'el-icon-document']"></i>
                  <span class="vtm-nav__label">{{ leaf.name }}</span>
                </router-link>
              </template>
            </div>
          </template>
          <!-- 收起态（图标模式）：分隔线 + 直接平铺管理项 -->
          <template v-else>
            <div class="vtm-nav__divider"></div>
            <template v-for="group in zones.admin">
              <router-link
                v-for="leaf in group.items"
                :key="'c' + leaf.id"
                :to="leaf.link"
                :class="['vtm-nav__item', { 'is-active': isActive(leaf.link) }]"
                :title="leaf.name"
              >
                <i :class="['vtm-nav__icon', leaf.icon || 'el-icon-document']"></i>
              </router-link>
            </template>
          </template>
        </template>
      </nav>
    </vue-scroll>

    <!-- 帮助与支持——突出显示；打开 Intercom 客服 -->
    <button class="vtm-nav__help" type="button" :title="collapse ? $t('nav.helpSupport') : ''" @click="openSupport">
      <i class="vtm-nav__help-icon iconfont icon-kefu"></i>
      <span v-if="!collapse" class="vtm-nav__help-text">
        <span class="vtm-nav__help-title">{{ $t("nav.helpSupport") }}</span>
        <span class="vtm-nav__help-sub">{{ $t("nav.helpSub") }}</span>
      </span>
    </button>
  </aside>
</template>

<script>
  import lineUtil from "@/utils/humpToLine";
  import { resolveMenuName } from "@/utils/menuLabels";
  import { classifyNav, isEssential, isHiddenNav, grantedLinks, ESSENTIAL_COMPONENTS, NAV_ICONS, NAV_LABEL_OVERRIDES, NAV_LINK_REDIRECTS, NAV_SEARCH_ALIASES, LOG_HUB_LINKS } from "@/utils/navSections";

  const ADMIN_OPEN_KEY = "vtm_nav_admin_open";

  export default {
    name: "Aside",
    data() {
      let adminOpen = false;
      try {
        adminOpen = localStorage.getItem(ADMIN_OPEN_KEY) === "1";
      } catch (e) {
        /* ignore */
      }
      return {
        menuQuery: "",
        adminOpen,
        // API & AI 入口：仅当后端 apiKey/access 允许时显示（软发布白名单）
        apiAccess: false,
        // 品牌标志：展开用横版锁形图，收起用方形标记
        fullLogo: require("../../assets/img/logo-white.png"),
        markLogo: require("../../assets/img/logo-mark-white.png")
      };
    },
    computed: {
      collapse() {
        return this.$store.state.collapse;
      },
      // 后端菜单树 → 分组结构（沿用原契约：顶层无子级归入无标签首组）
      allGroups() {
        const menu = this.$store.state.menuList || [];
        const groups = [];
        const rootItems = [];

        menu.forEach(node => {
          if (!node || node.hideInNav) {
            return;
          }
          if (node.children && node.children.length) {
            const items = node.children.map(child => this.toNavItem(child)).filter(Boolean);
            if (items.length) {
              groups.push({ id: node.id, label: node.name, items });
            }
          } else if (node.component) {
            rootItems.push(this.toNavItem(node));
          }
        });

        if (rootItems.length) {
          groups.unshift({ id: "__root__", label: "", items: rootItems.filter(Boolean) });
        }
        return groups;
      },
      // 已授权页面 link 集合（决定被枢纽吸收的旧页面是否可以安全隐藏）
      granted() {
        return grantedLinks(this.$store.state.menuList);
      },
      // 三区划分：pinned(置顶必备) / operator(运营者工具) / admin(管理员)
      baseZones() {
        const essentialsByComponent = {};
        const opBuckets = [];
        const adBuckets = [];
        const bucketIndex = {};

        const bucketFor = (list, key, label) => {
          if (!bucketIndex[key]) {
            const b = { id: key, label: label || "", items: [] };
            bucketIndex[key] = b;
            list.push(b);
          }
          return bucketIndex[key];
        };

        const routeLeaf = (leaf, group, subLabel) => {
          if (!leaf || !leaf.link) {
            return;
          }
          // 隐藏页面：不进入任何分区（被枢纽吸收的旧页面仅在枢纽已授权时隐藏）
          if (isHiddenNav(leaf, this.granted)) {
            return;
          }
          if (isEssential(leaf.component)) {
            essentialsByComponent[leaf.component] = leaf;
            return;
          }
          const zone = classifyNav(leaf);
          const list = zone === "admin" ? adBuckets : opBuckets;
          const key = group.id + (subLabel ? "|" + subLabel : "");
          const label = subLabel ? (group.label ? group.label + " · " + subLabel : subLabel) : group.label;
          bucketFor(list, key, label).items.push(leaf);
        };

        this.allGroups.forEach(group => {
          group.items.forEach(item => {
            if (item.children && item.children.length) {
              item.children.forEach(leaf => routeLeaf(leaf, group, item.name));
            } else {
              routeLeaf(item, group);
            }
          });
        });

        // 审计日志枢纽（/logs，前端静态路由）：只要六个旧页面中任意一个已授权，就在管理区顶部钉一个入口
        if (LOG_HUB_LINKS.some(link => this.granted.has(link))) {
          adBuckets.unshift({
            id: "__logsGroup__",
            label: "",
            items: [{ id: "__logs__", name: this.$t("nav.auditLogs"), icon: "el-icon-notebook-2", link: "/logs" }]
          });
        }
        // 支付方式（原「显示定制」启动器内的卡片；启动器已重定向直达主题管理）
        if (this.granted.has("/myapps/list")) {
          adBuckets.push({
            id: "__payGroup__",
            label: "",
            items: [{ id: "__payment__", name: this.$t("nav.methodOfPayment"), icon: "el-icon-bank-card", link: "/myapps/payment/index" }]
          });
        }

        // API & AI（前端静态页 /developers）
        if (this.apiAccess) {
          adBuckets.push({
            id: "__apiGroup__",
            label: "",
            items: [{ id: "__developers__", name: this.$t("nav.apiAndAi"), icon: "el-icon-connection", link: "/developers" }]
          });
        }

        // 置顶：首页 + 按既定顺序的必备项
        const pinned = [{ id: "__home__", name: this.$t("nav.home"), icon: "el-icon-s-home", link: "/main" }];
        ESSENTIAL_COMPONENTS.forEach(component => {
          if (essentialsByComponent[component]) {
            pinned.push(essentialsByComponent[component]);
          }
        });
        // Perks（前端静态页，全员可见）：批发优惠营销位
        pinned.push({ id: "__perks__", name: this.$t("nav.perks"), icon: "el-icon-present", link: "/perks", badge: this.$t("nav.new") });

        return { pinned, operator: opBuckets, admin: adBuckets };
      },
      // 查找模式：按名称过滤三区
      zones() {
        const query = this.menuQuery.toLowerCase();
        const base = this.baseZones;
        if (!query) {
          return base;
        }
        const matches = leaf =>
          String(leaf.name || "").toLowerCase().includes(query) ||
          String(NAV_SEARCH_ALIASES[String(leaf.link || "").toLowerCase()] || "").includes(query);
        const filterGroups = groups =>
          groups
            .map(group => {
              const items = group.items.filter(leaf => matches(leaf));
              return items.length ? { ...group, items } : null;
            })
            .filter(Boolean);
        return {
          pinned: base.pinned.filter(item => matches(item)),
          operator: filterGroups(base.operator),
          admin: filterGroups(base.admin)
        };
      }
    },
    watch: {
      // 从经典版切回/重新登录时菜单晚于侧栏加载：菜单就绪后再查一次入口权限
      "$store.state.menuList"() {
        this.loadApiAccess();
      },
      // 进入管理员页面时自动展开管理区，确保当前项可见
      $route: {
        immediate: true,
        handler(route) {
          if (route && classifyNav({ path: route.path }) === "admin") {
            this.adminOpen = true;
          }
          if (route && route.path === "/developers") {
            this.adminOpen = true;
            if (!this.apiAccess) {
              this.loadApiAccess();
            }
          }
        }
      }
    },
    created() {
      this.loadApiAccess();
    },
    methods: {
      loadApiAccess() {
        this.$api
          .request(this.$url.ApiKey.access, this.$method.get)
          .then(res => {
            this.apiAccess = !!(res && res.code === 0 && res.data && res.data.allowed);
          })
          .catch(() => {
            this.apiAccess = false;
          });
      },
      toNavItem(node) {
        if (!node || node.hideInNav) {
          return null;
        }
        if (node.children && node.children.length) {
          return {
            id: node.id,
            name: resolveMenuName(node),
            icon: node.icon,
            children: node.children.map(child => this.toNavItem(child)).filter(Boolean)
          };
        }
        if (!node.component) {
          return null;
        }
        let link = node.path + "/" + lineUtil.toLine(node.component);
        // 按 link 重定向导航目标（如「显示定制」启动器 → 直达主题管理）
        const redirect = NAV_LINK_REDIRECTS[String(link).toLowerCase()];
        if (redirect) {
          link = redirect;
        }
        // 按 link 覆盖文案（如「显示定制」直达主题管理）
        const labelKey = NAV_LABEL_OVERRIDES[String(link).toLowerCase()];
        return {
          id: node.id,
          name: labelKey ? this.$t(labelKey) : resolveMenuName(node),
          // 图标优先用前端覆盖表（后端图标多雷同），无覆盖再回退后端图标
          icon: NAV_ICONS[node.component] || node.icon,
          component: node.component,
          path: node.path,
          link
        };
      },
      isActive(link) {
        if (link === "/main") {
          return this.$route.path === "/main" || this.$route.path === "/";
        }
        return this.$route.path === link;
      },
      goHome() {
        if (this.$route.path !== "/main") {
          this.$router.push("/main");
        }
      },
      toggleAdmin() {
        this.adminOpen = !this.adminOpen;
        try {
          localStorage.setItem(ADMIN_OPEN_KEY, this.adminOpen ? "1" : "0");
        } catch (e) {
          /* ignore */
        }
      },
      openSupport() {
        // 打开帮助与支持中心（SupportHub 抽屉，在 Index.vue 中挂载）
        this.$store.commit("set", { supportVisible: true });
      }
    }
  };
</script>

<style scoped lang="scss">
  .vtm-nav {
    display: flex;
    flex-direction: column;
    width: 232px;
    flex-shrink: 0;
    height: 100%;
    background: var(--vtm-nav-bg);
    transition: width 0.2s ease;
    overflow: hidden;
  }

  .vtm-nav--collapsed {
    width: 64px;
  }

  .vtm-nav__brand {
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 14px 18px 10px;
    cursor: pointer;
    flex-shrink: 0;
  }

  .vtm-nav__logo {
    display: block;
    height: 40px;
    max-width: 100%;
    object-fit: contain;
  }

  // 收起态：方形标记，同时收紧内边距让标记有呼吸空间
  .vtm-nav--collapsed .vtm-nav__brand {
    padding: 14px 8px 10px;
  }

  .vtm-nav--collapsed .vtm-nav__logo {
    height: 26px;
  }

  .vtm-nav__search {
    display: flex;
    align-items: center;
    gap: 7px;
    margin: 0 14px 6px;
    padding: 6px 10px;
    flex-shrink: 0;
    border-radius: var(--vtm-r-sm);
    background: rgba(255, 255, 255, 0.07);
    color: var(--vtm-nav-text);

    > i {
      font-size: 13px;
      opacity: 0.7;
    }

    input {
      flex: 1;
      min-width: 0;
      border: none;
      background: transparent;
      color: #ffffff;
      font-size: 12.5px;
      outline: none;

      &::placeholder {
        color: var(--vtm-nav-group);
      }
    }
  }

  .vtm-nav__search-clear {
    display: inline-flex;
    border: none;
    background: transparent;
    color: var(--vtm-nav-text);
    cursor: pointer;
    padding: 0;
    font-size: 12px;

    &:hover {
      color: #ffffff;
    }
  }

  .vtm-nav__items {
    display: flex;
    flex-direction: column;
    padding: 4px 0 16px;
  }

  .vtm-nav__group {
    padding: 16px 18px 6px;
    font-family: var(--vtm-mono);
    font-size: 9.5px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: var(--vtm-nav-group);
    white-space: nowrap;
  }

  .vtm-nav__divider {
    height: 1px;
    margin: 10px 14px;
    background: rgba(255, 255, 255, 0.08);
  }

  .vtm-nav__subgroup {
    padding: 10px 18px 4px;
    font-size: 11px;
    font-weight: 600;
    color: var(--vtm-nav-group);
    white-space: nowrap;
  }

  .vtm-nav__item {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 18px;
    border: none;
    border-left: 3px solid transparent;
    background: transparent;
    color: var(--vtm-nav-text);
    font-family: var(--vtm-body);
    font-size: 13px;
    line-height: 1.4;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color var(--vtm-speed) ease, color var(--vtm-speed) ease;
    text-align: left;
    width: 100%;
    box-sizing: border-box;
  }

  /* 置顶必备项：略大、加粗，视觉层级更高 */
  .vtm-nav__item--pinned {
    font-size: 13.5px;
    font-weight: 600;
    color: #eaf2f0;
    padding-top: 9px;
    padding-bottom: 9px;

    .vtm-nav__icon {
      opacity: 1;
      color: var(--vtm-mint);
      font-size: 16px;
    }
  }

  .vtm-nav__item--nested {
    padding-left: 26px;
  }

  .vtm-nav--collapsed .vtm-nav__item,
  .vtm-nav--collapsed .vtm-nav__item--nested {
    justify-content: center;
    padding-left: 0;
    padding-right: 0;
  }

  .vtm-nav__item:hover {
    color: var(--vtm-nav-text-hover);
    background: var(--vtm-nav-hover-bg);
  }

  .vtm-nav__item.is-active {
    color: #ffffff;
    background: var(--vtm-nav-active-bg);
    border-left-color: var(--vtm-mint);
    font-weight: 600;
  }

  .vtm-nav__item:focus-visible {
    outline: 2px solid var(--vtm-mint);
    outline-offset: -2px;
  }

  .vtm-nav__icon {
    width: 17px;
    flex-shrink: 0;
    text-align: center;
    font-size: 15px;
    opacity: 0.9;
  }

  .vtm-nav__label {
    overflow: hidden;
    text-overflow: ellipsis;
  }

  /* 置顶项角标（如 Perks 的 NEW） */
  .vtm-nav__badge {
    margin-left: auto;
    padding: 1px 6px;
    border-radius: 999px;
    background: var(--vtm-mint);
    color: var(--vtm-navy);
    font-size: 9.5px;
    font-weight: 700;
    letter-spacing: 0.08em;
  }

  /* 管理员折叠头 */
  .vtm-nav__admintoggle {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    box-sizing: border-box;
    margin-top: 14px;
    padding: 10px 18px;
    border: none;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
    background: transparent;
    color: var(--vtm-nav-group);
    font-family: var(--vtm-mono);
    font-size: 10px;
    font-weight: 600;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    cursor: pointer;
    transition: color var(--vtm-speed) ease;

    &:hover {
      color: #ffffff;
    }

    .vtm-nav__icon {
      font-size: 14px;
    }

    .vtm-nav__label {
      flex: 1;
      text-align: left;
    }

    .vtm-nav__chev {
      font-size: 12px;
      opacity: 0.8;
    }

    &:focus-visible {
      outline: 2px solid var(--vtm-mint);
      outline-offset: -2px;
    }
  }

  .vtm-nav__adminbody {
    padding-top: 2px;
  }

  /* 帮助与支持——突出显示的底部按钮 */
  .vtm-nav__help {
    display: flex;
    align-items: center;
    gap: 10px;
    flex-shrink: 0;
    margin: 8px 14px 14px;
    padding: 10px 12px;
    border: 1px solid rgba(1, 225, 168, 0.35);
    border-radius: var(--vtm-r-sm);
    background: rgba(1, 225, 168, 0.12);
    color: #eaf2f0;
    cursor: pointer;
    text-align: left;
    transition: background-color var(--vtm-speed) ease, border-color var(--vtm-speed) ease;

    &:hover {
      background: rgba(1, 225, 168, 0.22);
      border-color: var(--vtm-mint);
    }

    &:focus-visible {
      outline: 2px solid var(--vtm-mint);
      outline-offset: 2px;
    }
  }

  .vtm-nav--collapsed .vtm-nav__help {
    justify-content: center;
    margin: 8px 10px 14px;
    padding: 10px 0;
  }

  .vtm-nav__help-icon {
    flex-shrink: 0;
    font-size: 20px;
    color: var(--vtm-mint);
  }

  .vtm-nav__help-text {
    display: flex;
    flex-direction: column;
    line-height: 1.25;
    min-width: 0;
  }

  .vtm-nav__help-title {
    font-size: 13px;
    font-weight: 600;
    color: #ffffff;
  }

  .vtm-nav__help-sub {
    font-size: 11px;
    color: var(--vtm-nav-text);
    overflow: hidden;
    text-overflow: ellipsis;
  }
</style>
